import React, { useState } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";

import { AvatarStack } from "../../../../components/visuals/Avatar";

import { CompassParagraph } from "../../../../components/common/styledComponents";
import {
  compassBordeaux,
  compassLightBlue,
  compassGreyBackground,
  compassLightGreen,
  compassLightGreenBackground,
  compassLightGreyBackground,
  compassLightRed,
  compassYellow,
  compassYellowBackground,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
  lightGreys,
} from "../../../../styles";
import {
  AssessmentStateV2,
  ConfidenceV2,
} from "../../../../types/engagementAssessmentV2Types";

export const FlagGlyph = ({ size = 11 }: { size?: number }): JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M1.875 11.25a.375.375 0 0 1-.375-.375V1.597c0-.098.026-.195.075-.28a.56.56 0 0 1 .204-.206C2.063.946 2.634.75 3.75.75c.872 0 1.848.345 2.708.649.693.245 1.348.476 1.792.476a4.3 4.3 0 0 0 1.664-.34.375.375 0 0 1 .586.389v5.141a.47.47 0 0 1-.281.429c-.204.09-.95.381-1.969.381-.566 0-1.275-.167-2.025-.345C5.382 7.331 4.51 7.125 3.75 7.125c-.864 0-1.306.131-1.5.213v3.537a.375.375 0 0 1-.375.375Z" />
  </svg>
);

const Stack = styled.span`
  display: inline-flex;
  align-items: center;

  .ant-avatar {
    cursor: default !important;
  }
  .ant-avatar-group > *:not(:first-child) {
    margin-inline-start: -3px;
  }
`;

export const FlagStack = ({ flags }: { flags: boolean[] }): JSX.Element => {
  const ordered = [...flags].sort((a, b) => Number(b) - Number(a));
  const count = flags.filter(Boolean).length;
  return (
    <Tooltip title={`${count} of ${flags.length} red flags`} placement="top">
      <Stack aria-label={`${count} of ${flags.length} red flags`}>
        <AvatarStack
          items={ordered.map((flagged) => ({
            username: "",
            size: "xxsmall",
            bgColor: flagged ? compassBordeaux : compassLightGreenBackground,
          }))}
          maxCount={flags.length}
          enableExpand={false}
        />
      </Stack>
    </Tooltip>
  );
};

const STATE_COLORS: Record<
  AssessmentStateV2,
  { bg: string; ink: string }
> = {
  STRONG: { bg: compassLightGreenBackground, ink: compassLightGreen },
  ADEQUATE: { bg: compassLightGreenBackground, ink: compassLightGreen },
  MIXED: { bg: compassYellowBackground, ink: compassYellow },
  WEAK: { bg: compassLightRed, ink: compassBordeaux },
  NOT_RATABLE: { bg: compassGreyBackground, ink: greyText },
};

export const stateInk = (state: AssessmentStateV2): string =>
  state === "NOT_RATABLE" ? lightGreyOutline : STATE_COLORS[state].ink;

const BadgePill = styled.span<{ $bg: string; $ink: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $bg }): string => $bg};
  color: ${({ $ink }): string => $ink};
  font-family: "ApercuMedium";
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const StateBadge = ({
  state,
}: {
  state: AssessmentStateV2;
}): JSX.Element => {
  const c = STATE_COLORS[state];
  return (
    <BadgePill $bg={c.bg} $ink={c.ink}>
      {state.replace(/_/g, " ")}
    </BadgePill>
  );
};

const GRADE_LEVELS = 4;

const GRADE: Record<
  AssessmentStateV2,
  { level: number; label: string; ink: string }
> = {
  STRONG: { level: 4, label: "Strong", ink: compassLightGreen },
  ADEQUATE: { level: 3, label: "Adequate", ink: compassLightGreen },
  MIXED: { level: 2, label: "Mixed", ink: compassYellow },
  WEAK: { level: 1, label: "Weak", ink: compassBordeaux },
  NOT_RATABLE: { level: 0, label: "Not ratable", ink: lightGreyOutline },
};

const Grade = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const GradeLabel = styled.span<{ $muted: boolean }>`
  min-width: 64px;
  text-align: right;
  font-family: "Apercu";
  font-size: 11.5px;
  color: ${({ $muted }): string => ($muted ? lightGreyOutline : lightGreyText)};
  white-space: nowrap;
`;

const Meter = styled.span`
  display: inline-flex;
  gap: 3px;
`;

const Notch = styled.span<{ $fill: string }>`
  width: 12px;
  height: 3px;
  border-radius: 3px;
  background: ${({ $fill }): string => $fill};
`;

export const GradeChip = ({
  state,
}: {
  state: AssessmentStateV2;
}): JSX.Element => {
  const g = GRADE[state];
  return (
    <Grade aria-label={`Grade: ${g.label}`}>
      <GradeLabel $muted={state === "NOT_RATABLE"}>{g.label}</GradeLabel>
      <Meter aria-hidden="true">
        {Array.from({ length: GRADE_LEVELS }, (_, i) => (
          <Notch key={i} $fill={i < g.level ? g.ink : lightGreyOutline} />
        ))}
      </Meter>
    </Grade>
  );
};

const ConfChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${compassLightGreyBackground};
  font-family: "Apercu";
  font-size: 10px;
  letter-spacing: 0.04em;
  color: ${greyText};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ConfValue = styled.span`
  font-family: "ApercuMono", "Apercu", monospace;
  color: ${darkGreen};
`;

export const ConfidenceChip = ({
  confidence,
  pct,
}: {
  confidence: ConfidenceV2;
  pct?: number | null;
}): JSX.Element => (
  <ConfChip title={`${titleCaseWord(confidence)} confidence`}>
    <span>CONF</span>
    <ConfValue>{pct != null ? `${pct}%` : confidence}</ConfValue>
  </ConfChip>
);

const titleCaseWord = (s: string): string =>
  s.charAt(0) + s.slice(1).toLowerCase();

export const SectionCaption = styled.span`
  font-family: "ApercuMedium";
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${greyText};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 0;
`;

const RefChip = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 320px;
  padding: 2px 7px;
  border-radius: 5px;
  border: 1px solid ${lightGreyOutline};
  background: ${lightGreys.KPIBackground};
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 9.5px;
  color: ${greyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const shortenRef = (ref: string): string => {
  const parts = ref.split(":");
  if (parts.length <= 1) return ref.length > 40 ? `${ref.slice(0, 38)}…` : ref;
  const kind = parts[0];
  const tail = parts[parts.length - 1];
  if (parts.length === 2) {
    return `${kind}:${tail.length > 20 ? `…${tail.slice(-18)}` : tail}`;
  }
  return `${kind}:…:${tail}`;
};

export const EvidenceChips = ({
  refs,
  max = 4,
}: {
  refs: string[];
  max?: number;
}): JSX.Element | null => {
  if (refs.length === 0) return null;
  const shown = refs.slice(0, max);
  const overflow = refs.length - shown.length;
  return (
    <ChipRow>
      {shown.map((ref, i) => (
        <RefChip key={`${ref}-${i}`} title={ref}>
          {shortenRef(ref)}
        </RefChip>
      ))}
      {overflow > 0 && <RefChip>+{overflow} more</RefChip>}
    </ChipRow>
  );
};

const DotRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const Dot = styled.span<{ $on: boolean; $tint: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $on, $tint }): string =>
    $on ? $tint : lightGreyOutline};
`;

export const MicroDots = ({
  total,
  active,
  tint,
}: {
  total: number;
  active: number;
  tint: string;
}): JSX.Element => (
  <DotRow>
    {Array.from({ length: total }).map((_, i) => (
      <Dot key={i} $on={i < active} $tint={tint} />
    ))}
  </DotRow>
);

const SegRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const Seg = styled.span<{ $tint: string }>`
  width: 9px;
  height: 14px;
  border-radius: 2px;
  background: ${({ $tint }): string => $tint};
`;

export const MicroStateBar = ({
  states,
}: {
  states: AssessmentStateV2[];
}): JSX.Element => (
  <SegRow>
    {states.map((s, i) => (
      <Seg key={i} $tint={stateInk(s)} title={s.replace(/_/g, " ")} />
    ))}
  </SegRow>
);

export const JudgementText = styled(CompassParagraph)`
  line-height: 1.5;
`;

const JudgementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TruncatedJudgement = styled(CompassParagraph)<{
  $maxLines: number;
  $isExpanded: boolean;
}>`
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $isExpanded, $maxLines }): string =>
    $isExpanded ? "unset" : String($maxLines)};
  -webkit-box-orient: vertical;
`;

const ExpandToggle = styled.span`
  color: ${compassLightBlue};
  font-size: 11px;
  cursor: pointer;
  font-family: "Apercu";
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

export const ExpandableJudgement = ({
  text,
  maxLines = 2,
  fontSize = 13,
}: {
  text: string;
  maxLines?: number;
  fontSize?: number;
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canExpand = text.length > maxLines * 90;

  return (
    <JudgementWrapper>
      <TruncatedJudgement
        $maxLines={maxLines}
        $isExpanded={isExpanded}
        fontSize={fontSize}
        color={greyText}
        lh={1.5}
        mr={0}
      >
        {text}
      </TruncatedJudgement>
      {canExpand && (
        <ExpandToggle
          role="button"
          tabIndex={0}
          onClick={(e): void => {
            e.stopPropagation();
            setIsExpanded((v) => !v);
          }}
          onKeyDown={(e): void => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              setIsExpanded((v) => !v);
            }
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </ExpandToggle>
      )}
    </JudgementWrapper>
  );
};
